import React, { useState, useEffect } from 'react';
import TopicInput from './TopicInput';
import VotingSection from './VotingSection';
import RebuttalGenerator from './RebuttalGenerator';
import BiasIndicator from './BiasIndicator';
import DebateCard from './DebateCard';
import { fetchArguments } from '../services/perplexityService';
import { analyzeSourcesBias } from '../services/biasService';
import '../styles/main.css';

const App = () => {
    const [topic, setTopic] = useState('');
    const [debateData, setDebateData] = useState(null);
    const [selectedArgument, setSelectedArgument] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingStates, setLoadingStates] = useState({
        for: false,
        against: false,
        bias: false
    });
    const [loadingStage, setLoadingStage] = useState(0);
    const [error, setError] = useState('');
    const [biasData, setBiasData] = useState(null);
    const [showShareCard, setShowShareCard] = useState(false);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const topicParam = urlParams.get('topic');
        if (topicParam) {
            handleTopicSubmit(topicParam);
        }
    }, []);

    const loadingMessages = [
        `Searching for arguments about "${topic}"...`,
        `Finding sources on "${topic}"...`,
        `Analyzing perspectives on "${topic}"...`,
        `Organizing arguments for "${topic}"...`,
        `Almost ready with "${topic}" debate...`,
    ];
    
    useEffect(() => {
        let interval;
        if (loading) {
            interval = setInterval(() => {
                setLoadingStage(prev => (prev + 1) % loadingMessages.length);
            }, 3000); 
        }
        return () => clearInterval(interval);
    }, [loading, loadingMessages.length]);

    const handleTopicSubmit = async (newTopic) => {
        setTopic(newTopic);
        setLoading(true);
        setLoadingStates({ for: false, against: false, bias: false });
        setLoadingStage(0);
        setError('');
        setBiasData(null);
        setShowShareCard(false);
        
        try {
            setTimeout(() => {
                setLoadingStates(prev => ({ ...prev, for: true }));
            }, 2000);
            
            const result = await fetchArguments(newTopic);
            
            const data = {
                topic: newTopic,
                argumentsFor: result.for,
                argumentsAgainst: result.against
            };
            
            setDebateData(data);
            
            setLoadingStates(prev => ({ ...prev, against: true, bias: false }));
            
            const allSources = [
                ...(result.for.flatMap(arg => arg.sources || [])),
                ...(result.against.flatMap(arg => arg.sources || []))
            ];
            
            if (allSources.length > 0) {
                try {
                    const biasResult = await analyzeSourcesBias(allSources);
                    setBiasData(biasResult);
                } catch (biasError) {
                    console.error("Error analyzing bias:", biasError);
                }
            }
            
            setLoadingStates(prev => ({ ...prev, bias: true }));
        } catch (err) {
            console.error("Error fetching debate data:", err);
            setError('Failed to fetch debate arguments. Please try again.');
        } finally {
            setLoading(false);
            setLoadingStates({ for: false, against: false, bias: false });
        }
    };

    const handleArgumentSelect = (argument) => {
        setSelectedArgument(argument);
    };

    const toggleShareCard = () => {
        setShowShareCard(!showShareCard);
    };

    const formatText = (text) => {
        if (!text) return null;
        
        return text.split('\n').map((paragraph, i) => 
            paragraph.trim() ? <p key={i}>{paragraph}</p> : null
        );
    };

    return (
        <div className="app-container">
            <header className="app-header">
                <h1>Debate Bot</h1>
                <p className="app-subtitle">AI that debates both sides so you can decide</p>
            </header>
            
            <TopicInput onSubmit={handleTopicSubmit} />
            
            {loading && (
                <div className="loading-container">
                    <div className="progress-container">
                        <div className="progress-bar"></div>
                    </div>
                    <p className="progress-text">{loadingMessages[loadingStage]}</p>
                    <p className="progress-indicator">
                        {loadingStates.for ? "✓ Arguments For loaded" : "⌛ Loading arguments for..."}
                        <br/>
                        {loadingStates.against ? "✓ Arguments Against loaded" : "⌛ Loading arguments against..."}
                        {loadingStates.bias !== undefined && (
                            <>
                                <br/>
                                {loadingStates.bias ? "✓ Bias analysis complete" : "⌛ Analyzing source bias..."}
                            </>
                        )}
                    </p>
                </div>
            )}
            
            {error && <div className="error-message">{error}</div>}
            
            {!loading && debateData && (
                <>
                    <h2 className="debate-topic">
                        Topic: {topic}
                        <button onClick={toggleShareCard} className="share-debate-button">
                            {showShareCard ? 'Hide Share Card' : 'Create Share Card'}
                        </button>
                    </h2>
                    
                    {showShareCard && (
                        <DebateCard debateData={debateData} biasData={biasData} />
                    )}
                    
                    <div className="debate-container">
                        <div className="arguments-column arguments-for">
                            <h2>Arguments For</h2>
                            {debateData.argumentsFor && debateData.argumentsFor.map((arg, index) => (
                                <div 
                                    key={`for-${index}`} 
                                    className="argument-card"
                                    onClick={() => handleArgumentSelect(arg)}
                                >
                                    <h3>{arg.title || `Argument ${index + 1}`}</h3>
                                    <div className="argument-text">
                                        {formatText(arg.text)}
                                    </div>
                                    {arg.sources && arg.sources.length > 0 && (
                                        <div className="sources">
                                            <h4>Sources:</h4>
                                            <ul>
                                                {arg.sources.map((source, idx) => (
                                                    <li key={idx}>
                                                        <a href={source.link} target="_blank" rel="noopener noreferrer">
                                                            {source.title || source.link}
                                                        </a>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        
                        <div className="arguments-column arguments-against">
                            <h2>Arguments Against</h2>
                            {debateData.argumentsAgainst && debateData.argumentsAgainst.map((arg, index) => (
                                <div 
                                    key={`against-${index}`} 
                                    className="argument-card"
                                    onClick={() => handleArgumentSelect(arg)}
                                >
                                    <h3>{arg.title || `Argument ${index + 1}`}</h3>
                                    <div className="argument-text">
                                        {formatText(arg.text)}
                                    </div>
                                    {arg.sources && arg.sources.length > 0 && (
                                        <div className="sources">
                                            <h4>Sources:</h4>
                                            <ul>
                                                {arg.sources.map((source, idx) => (
                                                    <li key={idx}>
                                                        <a href={source.link} target="_blank" rel="noopener noreferrer">
                                                            {source.title || source.link}
                                                        </a>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    {biasData && (
                        <div className="bias-analysis-section">
                            <h3>Source Bias Analysis</h3>
                            <BiasIndicator biasData={biasData} />
                        </div>
                    )}
                    
                    {selectedArgument && (
                        <RebuttalGenerator selectedArgument={selectedArgument} />
                    )}
                    
                    <VotingSection debateData={debateData} />
                </>
            )}
        </div>
    );
};

export default App;